/**
 * Veterinary patient age.
 *
 * WHY THIS FILE EXISTS: DICOM PatientAge (0010,1010) is VR AS — exactly four characters, three
 * digits and one unit suffix, `nnnD` | `nnnW` | `nnnM` | `nnnY` — and the SENDER picks the unit.
 * All four are conformant. We only ever expected Y and M, so a twelve year old dog described as
 * "626W" (626 weeks) had that string rendered verbatim on the worklist, the viewer banner and the
 * reporting rail. A submitting clinic wrote in to ask what they were doing wrong. They were doing
 * nothing wrong: on the study that prompted it the age tag was absent entirely and the birth date
 * was populated, so the weeks value had been rendered upstream. Ingest now derives from the birth
 * date where it can; this module is what makes whatever DOES reach the browser readable.
 *
 * THE UNIT RULE, and it is the whole reason this is a ladder and not a divide-by-seven: a fine
 * unit is CORRECT at small magnitudes and absurd at large ones. "10W" for a neonate is exactly
 * what a vet wants to read and must survive untouched; "626W" for an adult is noise. A value is
 * promoted to a coarser unit only once it outgrows the one it arrived in.
 *
 * THE PRESERVATION RULE: anything that does not parse is returned trimmed and otherwise
 * UNTOUCHED, never blanked. An unrecognised age string is still the only age information anybody
 * has, and a human can read "18 months" where this parser cannot.
 *
 * Mirrors backend `Shared.Utilities.PatientAge` exactly, the same way `patientSex.ts` mirrors
 * `Normalizers.SexAndNeuter`, so a value renders the same whether it was normalized at ingest or
 * is a legacy row that predates that change. Study rows written before normalization shipped still
 * hold raw AS values and are NOT backfilled, so this module carries the full ladder rather than
 * assuming the backend already climbed it. If you change a threshold here, change it there.
 */
/** Below this many days, days are the honest unit (a neonate's first fortnight). */
const KEEP_DAYS_BELOW = 14;
/** Below this many days (~16 weeks), weeks are the honest unit — puppy/kitten range. */
const KEEP_WEEKS_BELOW = 112;
/** Below this many months, months are the honest unit. Matches backend `KeepMonthsBelow`. */
const KEEP_MONTHS_BELOW = 12;
/** Mean days per Gregorian year, as the Julian 365.25. Matches backend `DaysPerYear`. */
const DAYS_PER_MONTH = 365.25 / 12;
/** Nothing plausible as an age needs ten digits; keeps a mis-mapped column out of the maths. */
const MAX_DIGITS = 9;
/** `2Y6M` — the two-component canonical form the backend emits. */
const COMBINED = /^(\d{1,9})\s*[Yy]\s*(\d{1,9})\s*[Mm]$/;
/** `626W`, `005Y`, `8M`, or a bare number (read as years). */
const SINGLE = /^(\d{1,9})\s*([DdWwMmYy])?$/;
/** Canonical compact form for a whole number of months. Mirrors backend `FromMonths`. */
function fromMonths(totalMonths) {
    if (totalMonths <= 0)
        return [{ value: 0, unit: 'mo' }];
    if (totalMonths < KEEP_MONTHS_BELOW)
        return [{ value: totalMonths, unit: 'mo' }];
    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;
    return months === 0
        ? [{ value: years, unit: 'y' }]
        : [
            { value: years, unit: 'y' },
            { value: months, unit: 'mo' },
        ];
}
/** Canonical compact form for a whole number of days. Mirrors backend `FromDays`. */
function fromDays(totalDays) {
    if (totalDays <= 0)
        return [{ value: 0, unit: 'd' }];
    if (totalDays < KEEP_DAYS_BELOW)
        return [{ value: totalDays, unit: 'd' }];
    if (totalDays < KEEP_WEEKS_BELOW)
        return [{ value: Math.round(totalDays / 7), unit: 'wk' }];
    // Rounded, not truncated. 626W is 4382 days, which truncates to 143 months (11y 11mo) and
    // rounds to 144 (12y). The sender derived those weeks from a real birth date, so the nearest
    // month is the honest reading and the one a vet would have written.
    return fromMonths(Math.round(totalDays / DAYS_PER_MONTH));
}
/**
 * Parse a raw or canonical age into its display components, climbing the unit ladder.
 * Returns null for absent, blank, or unparseable input — callers that need to preserve an
 * unparseable value should use {@link formatPatientAge}, which falls back to the raw string.
 */
export function parsePatientAge(raw) {
    const trimmed = raw?.trim();
    if (!trimmed)
        return null;
    const combined = COMBINED.exec(trimmed);
    if (combined) {
        const years = Number(combined[1]);
        const months = Number(combined[2]);
        if (!Number.isSafeInteger(years) || !Number.isSafeInteger(months))
            return null;
        return fromMonths(years * 12 + months);
    }
    const single = SINGLE.exec(trimmed);
    if (!single)
        return null;
    const value = Number(single[1]);
    if (!Number.isSafeInteger(value) || single[1].length > MAX_DIGITS)
        return null;
    // A bare number is read as YEARS. That is what every sender omitting the suffix has meant, and
    // it is the only reading that cannot turn a puppy into an adult — reading "5" as 5 days would.
    switch ((single[2] ?? 'Y').toUpperCase()) {
        case 'D':
            return fromDays(value);
        case 'W':
            return fromDays(value * 7);
        case 'M':
            return fromMonths(value);
        default:
            return fromMonths(value * 12);
    }
}
/**
 * Age components from a date of birth, as of a given instant. Mirrors backend
 * `Normalizers.AgeFromDob`: calendar arithmetic, then the same compact form.
 *
 * Prefer this over the DICOM tag wherever a birth date exists. PatientAge (0010,1010) is age at
 * ACQUISITION — a frozen string that is correct for the study it came with and progressively
 * wrong on every prior study of the same patient. A birth date does not go stale.
 *
 * Returns null for an absent or unparseable date, or one in the future.
 */
export function patientAgeFromBirthDate(birthDate, asOf) {
    if (!birthDate)
        return null;
    const dob = birthDate instanceof Date ? birthDate : new Date(birthDate);
    if (Number.isNaN(dob.getTime()))
        return null;
    const now = asOf instanceof Date ? asOf : new Date(asOf);
    if (dob.getTime() > now.getTime())
        return null;
    // Calendar months, not a milliseconds/30.4375 approximation: the latter drifts by a day per
    // month and can report "11mo" on an animal's first birthday.
    let months = (now.getFullYear() - dob.getFullYear()) * 12 + (now.getMonth() - dob.getMonth());
    if (now.getDate() < dob.getDate())
        months--;
    return fromMonths(Math.max(months, 0));
}
const UNIT_KEY = {
    y: 'common:patientAge.unit.years',
    mo: 'common:patientAge.unit.months',
    wk: 'common:patientAge.unit.weeks',
    d: 'common:patientAge.unit.days',
};
/**
 * The age a vet reads at a glance: "12y", "2y 6mo", "8mo", "10wk", "13d".
 *
 * Absent input renders as an empty string so call sites can keep using their existing
 * truthiness checks and em-dash placeholders. Unparseable input renders as itself, trimmed.
 */
export function formatPatientAge(raw, t) {
    const trimmed = raw?.trim();
    if (!trimmed)
        return '';
    const parts = parsePatientAge(trimmed);
    if (!parts)
        return trimmed;
    return formatAgeParts(parts, t);
}
/** Render already-parsed components, for callers that derived them from a birth date. */
export function formatAgeParts(parts, t) {
    if (!parts || parts.length === 0)
        return '';
    return parts.map((p) => `${p.value}${t(UNIT_KEY[p.unit])}`).join('\u2009');
}
